import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {VRGroupService} from '../vrgroup.service';
import { interval, Observable, Subject, timer } from 'rxjs';
import { find, remove, uniqBy } from 'lodash-es';
import {SidebarPanelComponent} from '../../../sidebar/sidebar-panel/sidebar-panel.component';
import {AppMessageService} from '../../../shared/app-message/app-message.service';
import { _ } from '../../../shared/utils';
import { compareAsc, differenceInMinutes, isAfter, subMinutes } from 'date-fns/esm';
import { map, mapTo, shareReplay, startWith, takeUntil } from 'rxjs/operators';
import {LandingPageTile} from '../../../shared/tiles/home-tile.model';
import {PlusCode, Station} from '../common/station';
import {FilterEvent, FilterProperties, Filters} from '../../../sidebar/filters/filters.model';
import { StateService } from '../../../shared/state/state.service';
import {ScheduleTrain} from '../common/train';
import { AlarmMessage } from '../../../alarm/alarm-message';
import { AlarmService } from '../../../alarm/alarm.service';
import { ConflictAlarmTypes, TechnicalAlarmTypes } from '../../../alarm/alarm-definitions/interfaces';
import { InfoActionService } from '../../../element-utils/element-actions/info-action/info-action.service';
import { ITrainListComponent } from '../utils/train-list.type';
import { getScheduleTrainWithStatus } from '../utils/train.utils';

@Component({
  selector: 'qm-vrgroup-scheduled-trains',
  templateUrl: './scheduled-trains.component.html',
  styleUrls: ['./scheduled-trains.component.scss']
})
export class ScheduledTrainsComponent extends SidebarPanelComponent implements OnInit, OnDestroy, ITrainListComponent {
  public readonly columnWidths = {
    train: '4.7em',
    status: '1.2em',
    time: '6.7em',
    route: '6.5em'
  };

  public trainFilter: Map<string, FilterProperties[]> = new Map();
  public stations: string[] = [];
  public displayedTrains: ScheduleTrain[] = [];

  public trainTypeFilters = [
    {
      id: 1,
      name: _('Trains'),
      type: 'dropdown-multiple',
      filter_values: [
        {
          name: _('Commuter'),
          value: 'COMMUTER',
          boolean: true
        },
        {
          name: _('Long-distance'),
          value: 'LONGDISTANCE',
          boolean: true
        },
        {
          name: _('Freight train'),
          value: 'FREIGHT',
          boolean: true
        },
        {
          name: _('Locomotive'),
          value: 'LOCOMOTIVE',
          boolean: false
        },
        {
          name: _('Other'),
          value: 'SHUNTING',
          boolean: false
        },
      ]
    }
    // VGR-603, filter disabled (filter by default with max amount scheduled trains)
    // {
    //   id: 2,
    //   name: _('Departing in'),
    //   type: 'dropdown-single',
    //   filter_values: [
    //     {
    //       name: '30 ' + _('minutes'),
    //       value: '30',
    //       boolean: false
    //     },
    //     {
    //       name: '1 ' + _('hour'),
    //       value: '1',
    //       boolean: false
    //     },
    //     {
    //       name: '2 ' + _('hours'),
    //       value: '2',
    //       boolean: false
    //     },
    //     {
    //       name: '3 ' + _('hours'),
    //       value: '3',
    //       boolean: true
    //     }
    //   ]
    // }
  ];
  public typeAheadStations: Filters = {
    id: 1,
    name: 'Stations',
    type: 'type-ahead',
    filter_values: [],
    noFiltersShowsAll: true
  };
  public activeStations: FilterProperties[] = [];
  public alarmOverlaySelectedTrain: ScheduleTrain;
  public listLimit = 40;
  public currentDate$: Observable<Date> = timer(0, 1000)
    .pipe(
      map(() => new Date()),
      shareReplay(1)
    );
  private ngUnsubscribe: Subject<boolean> = new Subject();
  private allTrains: ScheduleTrain[];

  constructor(private vrGroupService: VRGroupService,
              private state: StateService,
              private changeDetector: ChangeDetectorRef,
              protected appMessageService: AppMessageService,
              private alarmService: AlarmService,
              private infoActionService: InfoActionService) {
    super(appMessageService);
    this.componentName = ScheduledTrainsComponent.getComponentName();
  }

  public static initTile(tile: LandingPageTile) {
    tile.description = _('Scheduled train departures for next hour');
    tile.icon = 'far fa-arrow-from-left';
    tile.title = _('Train departures');
    tile.panel = ScheduledTrainsComponent.getComponentName();
  }

  private static getComponentName(): string {
    return 'ScheduledTrains';
  }

  ngOnInit() {
    console.log('Schedules loading', this.loading);
    const temp = this.state.getPartOfState('scheludedTrains_filters');
    if (temp) {
      this.trainTypeFilters = temp[0];
      this.activeStations = temp[1];
    }
    for (const s of this.trainTypeFilters) {
      this.trainFilter.set(s.name, s.filter_values);
    }
    this.getStations();
    this.vrGroupService.scheduledTrainData$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(trains => {
      this.allTrains = trains;
      if (this.allTrains.length > 0) {
        this.loading = false;
      }
      this.displayedTrains = this.handleTrainScheduleUpdate(trains);
      this.changeDetector.markForCheck();
    });
  }

  /**
   * Angular destructor function
   */
  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private stationToFilterProperties(station: Station): FilterProperties {
    return {
      name: station.stationName + ' (' + station.stationShortCode + ')',
      value: station.stationShortCode,
      boolean: this.checkIfActive({ value: station.stationShortCode })
    };
  }

  private async getStations() {
    if (this.typeAheadStations.filter_values.length < 1) {
      const stations: Station[] = await this.vrGroupService.getStationList();
      const plusCodes = await this.vrGroupService.getPlusCodes();
      this.typeAheadStations.filter_values = stations
        .map(station => this.stationToFilterProperties(station))
        .concat(
          Array.from(plusCodes.values()).map(plus => ({
            name: `${plus.name} (${plus.code})`,
            value: plus.code,
            boolean: this.checkIfActive({ value: plus.code }),
          }))
        );
    }
    this.changeDetector.markForCheck();
  }

  private checkIfActive(testedStation: FilterProperties): boolean {
    // plus-codes are never selected; they get expanded into individual stations and that would cause conflicts
    const value = '' + testedStation.value;
    if (value.endsWith('+')) {
      return false;
    }
    return !!find(this.activeStations, activeStation => activeStation.value === testedStation.value);
  }

  public removeFromActives(s: any) {
    this.activeStations = remove(this.activeStations, (o: any) => {
      return o !== s;
    });
    this.typeAheadStations.filter_values.forEach((o: any) => {
      if (s.value === o.value) {
        o.boolean = false;
      }
    });
    this.saveFilterState([this.trainTypeFilters, this.activeStations]);
    this.displayedTrains = this.handleTrainScheduleUpdate(this.allTrains);
    this.changeDetector.markForCheck();
  }

  public async filtersChanged($event: FilterEvent): Promise<void> {
    if ($event) {
      const plusCodes = await this.vrGroupService.getPlusCodes();
      this.activeStations = uniqBy(
        $event.typeAheadResults.filter_values
        .flatMap(props => {
          // discard non-selected options
          if (!props.boolean) {
            return [];
          }

          // expand plus codes into individual stations
          const value = '' + props.value;
          if (value.endsWith('+')) {
            return plusCodes.get(value)
              .stations
              .map(station => this.stationToFilterProperties(station));
          }

          return [props];
        }),
        props => props.name,
      );
      this.typeAheadStations = {
        ...$event.typeAheadResults,
        filter_values: $event.typeAheadResults.filter_values.map(props => ({
          ...props,
          boolean: this.checkIfActive(props),
        }))
      };
      for (const s of $event.customFilters) {
        this.trainFilter.set(s.name, s.filter_values);
      }
      this.displayedTrains = this.handleTrainScheduleUpdate(this.allTrains);
      this.saveFilterState([$event.customFilters, this.activeStations]);
      this.changeDetector.markForCheck();
    }
  }

  private saveFilterState(filters: any) {
    this.state.updateState('scheludedTrains_filters', filters);
  }

  /**
   * Is train visible on list?
   * @param filterType
   * @param train
   * @returns {any}
   */
  public trainVisible(filterType: string, train: ScheduleTrain): boolean {
    for (const s of this.trainFilter.get(filterType)) {
      if (s.value === train.type) {
        if (this.activeStations.length > 0 && s.boolean) {
          return find(this.activeStations, (o: any) => {
            return o.value === train.depStation;
          });
        } else {
          return s.boolean;
        }
      }
    }
  }

  public diffFromNow = (train: ScheduleTrain) => {
    const currentDate = new Date();
    return differenceInMinutes(subMinutes(currentDate, 90), train.depTime);
  }

/**
 * Filter, enrich and sort trains
 */
  private handleTrainScheduleUpdate(trains: ScheduleTrain[]) {
    return trains
      .filter(train => this.trainVisible('Trains', train))
      .map(train => getScheduleTrainWithStatus(train))
      .sort((a: ScheduleTrain, b: ScheduleTrain) => compareAsc(a.depTime, b.depTime))
      .sort(train => train.flagged ? -1 : 0);
  }

  public getDependencyConflictForTrain = (train: ScheduleTrain) => {
    return this.findVrTrainConflictAlarms(train).find(alarm => {
      return alarm.metadata.active === true;
    });
  }

  public getDepartureAlarmForTrain = (train: ScheduleTrain) => {
    return train.alarms
      .filter(alarm => alarm.metadata.type === 'trainDeparture')
      .find(alarm => {
        return alarm.metadata.active === true;
      });
  }

  public selectAlarmOverlayTrain(train: ScheduleTrain) {
    this.alarmOverlaySelectedTrain = train;
  }

  public getTranslatedAlarm = (alarm: AlarmMessage) => {
    return this.alarmService.getTranslatedAlarm(alarm.metadata, false);
  }

  public findVrTrainConflictAlarms(train: ScheduleTrain) {
    return train.alarms.filter(alarm => {
      return ConflictAlarmTypes.indexOf(alarm.metadata.type) !== -1;
    });
  }

  public hasActiveTechnicalAlarms = (train: ScheduleTrain) => {
    return this.getActiveTechnicalAlarms(train).length > 0;
  }

  public getActiveTechnicalAlarms = (train: ScheduleTrain) => {
    return this.filterVrTrainTechnicalAlarms(train).filter(a => a.metadata.active);
  }

  public filterVrTrainTechnicalAlarms(train: ScheduleTrain) {
    return train.alarms.filter(alarm => {
      return TechnicalAlarmTypes.indexOf(alarm.metadata.type) !== -1;
    });
  }

  public async openTrainInfo(train: ScheduleTrain) {
    const el = await this.vrGroupService.getTrainMapElement(train.tripNumber, train.tripDate);
    this.infoActionService.openDetails(el.layer, el);
  }

  public async openCargoInfo(train: ScheduleTrain) {
    const el = await this.vrGroupService.getTrainMapElement(train.tripNumber, train.tripDate);
    this.infoActionService.openDetails(el.layer, el, 'cargomanifest');
  }

  public onLoadMoreBtnClicked() {
    this.listLimit += 30;
  }
}
